import { LOCAL_STORAGE_TAB_ID, SESSION_STORAGE_TAB_ID, END_OF_QUESTS_MODAL_SEEN_KEY } from 'root/lib/constants';
import { hasSeenInSession } from 'root/lib/modalSessionUtils';
import { MODAL_TYPES, MODAL_PRIORITY_MAP } from 'a/components/ModalSystem';
import {
  getEndOfLevelSummary,
  getLastAppLaunched,
  getIsEreaderComplete,
  getIsTFEPartComplete,
  getIsVocabSessionComplete,
  getIsTFEComplete,
} from 'a/hub/selectors/studentProgressSelectors';
import {
  getSuccessModalText,
  getEarlyExitModalText,
  getTFEEarlyExitModalText,
  getLoginDialogs,
} from 'a/hub/selectors/modalTextSelectors';
import {
  getWaitingListModals,
  getIsThisTypeOfModalOpen,
  getIsMultipleSessionsModalOpen,
} from 'a/hub/selectors/modalSystemSelectors';

import { getSelectedActivity } from 'a/hub/selectors/sessionSelectors';
import { getProduct } from 'a/selectors/languageSelectors';
import { getGamesForDisplay, getWorkshopStep } from 'a/hub/selectors/workshopSelectors';
import { PROGRESS_TYPE } from 'a/hub/reducers/studentProgressReducer';
import { reportMultipleSessions } from 'a/actions/searchlightActions';
import { advanceToNextWorkshopStep } from './workshopActions';
import { createMetaGameActionObj } from './questsActions';

export const openModal = (modalType, config = {}) => (dispatch, getState) => {
  // only allow 1 instance of each modal to be open
  const isAlreadyOpen = getIsThisTypeOfModalOpen(getState(), modalType);
  if (isAlreadyOpen) {
    return;
  }

  dispatch(createMetaGameActionObj(
    'OPEN_MODAL', {
      modalType,
      priority: config.priority || MODAL_PRIORITY_MAP[modalType] || 0,
      config,
    }),
  );
};

export const closeModal = modalType => createMetaGameActionObj('CLOSE_MODAL', modalType);

export const closeAllModals = () => createMetaGameActionObj('CLOSE_ALL_MODALS');

export const closeOpenModal = () => createMetaGameActionObj('CLOSE_OPEN_MODAL');

export const clearWaitingListModals = () => createMetaGameActionObj('CLEAR_WAITING_LIST_MODALS');

export const addModalToWaitingList = (modalType, config = {}) => createMetaGameActionObj(
  'ADD_MODAL_TO_WAITING_LIST', {
    modalType,
    config,
  });

export const openModalsInWaitingList = () => (dispatch, getState) => {
  const waitingListModals = getWaitingListModals(getState());
  waitingListModals.forEach(({ modalType, config = {} }) => {
    dispatch(openModal(modalType, config));
  });
  dispatch(clearWaitingListModals());
};

export const openTFELaunchModal = () => openModal(MODAL_TYPES.TFE_LAUNCH);

// Stamps the step the modal was opened for; advanceToNextWorkshopStep ignores a stale click
const openStepSuccessModal = modalType => (dispatch, getState) => {
  const state = getState();
  const { heading, text } = getSuccessModalText(state);
  dispatch(openModal(modalType, { heading, text, openedAtStep: getWorkshopStep(state) }));
};

export const openTFESuccessModal = () => openStepSuccessModal(MODAL_TYPES.TFE_SUCCESS);

export const openTFEEarlyExitModal = () => (dispatch, getState) => {
  const { heading, text } = getTFEEarlyExitModalText(getState());
  dispatch(openModal(MODAL_TYPES.TFE_EARLY_EXIT, { heading, text }));
};

export const openRePlacementModal = () => openModal(MODAL_TYPES.RE_PLACEMENT);

export const openNotificationModal = config => openModal(MODAL_TYPES.NOTIFICATION, config);

export const openGameSelectorModal = () => openModal(MODAL_TYPES.GAME_SELECTOR);

export const openFluencyLaunchModal = () => openModal(MODAL_TYPES.FLUENCY_LAUNCH);

export const openLoginModal = () => (dispatch, getState) => {
  const dialogs = getLoginDialogs(getState());
  dispatch(openModal(MODAL_TYPES.LOGIN, { dialogs }));
};

export const openEndOfQuestsModal = () => openModal(MODAL_TYPES.END_OF_QUESTS);

export const openChallengeQuestModal = () => openModal(MODAL_TYPES.CHALLENGE_QUEST);

export const openEndOfAllQuestsAndChallengesModal = () => (dispatch, getState) => {
  const product = getProduct(getState());

  // Only open modal if not already seen this session
  if (!hasSeenInSession(END_OF_QUESTS_MODAL_SEEN_KEY, product)) {
    dispatch(openModal(MODAL_TYPES.END_OF_ALL_QUESTS_AND_CHALLENGES));
  }
};

export const openChallengeQuestSuccessModal = () => openModal(MODAL_TYPES.CHALLENGE_QUEST_SUCCESS);

export const openVocabLaunchModal = () => openModal(MODAL_TYPES.VOCAB_LAUNCH);

export const openVocabSuccessModal = () => openStepSuccessModal(MODAL_TYPES.VOCAB_SUCCESS);

export const openVocabEarlyExitModal = () => (dispatch, getState) => {
  const { heading, text } = getEarlyExitModalText(getState());
  dispatch(openModal(MODAL_TYPES.VOCAB_EARLY_EXIT, { heading, text }));
};

export const openGameSuccessModal = ({ isInWaitingList = false }) => (dispatch, getState) => {
  const config = getSuccessModalText(getState());
  const method = isInWaitingList ? addModalToWaitingList : openModal;
  dispatch(method(MODAL_TYPES.GAME_SUCCESS, config));
};

export const openGameFailureModal = ({ isInWaitingList = false }) => (dispatch) => {
  const method = isInWaitingList ? addModalToWaitingList : openModal;
  dispatch(method(MODAL_TYPES.GAME_FAILURE));
};

export const openGameEarlyExitModal = (config, hasProgress) => (dispatch, getState) => {
  const status = hasProgress ? 'in_progress' : 'not_started';
  const { heading, text } = getEarlyExitModalText(getState(), status);
  dispatch(openModal(MODAL_TYPES.EARLY_EXIT, { ...config, heading, text }));
};

export const openEreaderLaunchModal = () => openModal(MODAL_TYPES.EREADER_LAUNCH);

export const openEreaderSuccessModal = () => openStepSuccessModal(MODAL_TYPES.EREADER_SUCCESS);
export const openEreaderEarlyExitModal = () => (dispatch, getState) => {
  const { heading, text } = getEarlyExitModalText(getState());
  dispatch(openModal(MODAL_TYPES.EREADER_EARLY_EXIT, { heading, text }));
};

export const openQuestCompleteModal = () => openModal(MODAL_TYPES.QUEST_COMPLETE);

export const openLogoutModal = () => (dispatch) => {
  dispatch(openModal(MODAL_TYPES.LOGOUT));
};

export const closeLogoutModal = () => closeModal(MODAL_TYPES.LOGOUT);

export const openGlobalNavLogoutModal = () => (dispatch) => {
  dispatch(openModal(MODAL_TYPES.GLOBAL_NAV_LOGOUT));
};

export const closeGlobalNavLogoutModal = () => closeModal(MODAL_TYPES.GLOBAL_NAV_LOGOUT);

export const openSessionTimeoutModal = config => openModal(MODAL_TYPES.SESSION_TIMEOUT, config);

export const closeSessionTimeoutModal = () => closeModal(MODAL_TYPES.SESSION_TIMEOUT);

export const openMultipleSessionsModal = () => (dispatch, getState) => {
  const state = getState();
  const isMultipleSessionsModalOpen = getIsMultipleSessionsModalOpen(state);
  if (isMultipleSessionsModalOpen) {
    return;
  }

  const gameId = getSelectedActivity(state);
  const action = 'modal_displayed';
  const localStorageId = localStorage.getItem(LOCAL_STORAGE_TAB_ID) ?? 'N/A';
  const sessionStorageId = sessionStorage.getItem(SESSION_STORAGE_TAB_ID) ?? 'N/A';

  dispatch(openModal(MODAL_TYPES.MULTIPLE_SESSIONS));
  dispatch(reportMultipleSessions(gameId, { action, localStorageId, sessionStorageId }));
};

export const closeMultipleSessionsModal = () => closeModal(MODAL_TYPES.MULTIPLE_SESSIONS);

export const openLevelSummaryModal = () => (dispatch, getState) => {
  const { correctCount, totalCount, isSuccess } = getEndOfLevelSummary(getState());
  dispatch(openModal(MODAL_TYPES.LEVEL_SUMMARY, { correctCount, totalCount, isSuccess }));
};

export const openWorkshopStartupModals = (config = {}) => (dispatch, getState) => {
  const state = getState();

  const { shouldShowReplacementModal } = state.workshopReducer;
  if (shouldShowReplacementModal) {
    dispatch(openRePlacementModal());
  }

  const { targetActivityId = '' } = config;
  const lastAppLaunched = getLastAppLaunched(state);

  if (targetActivityId.includes('skill_game')) {
    const games = getGamesForDisplay(state);
    const areAllGamesComplete = games.every(
      ({ levelsMastered, levels }) => levelsMastered.length >= levels.length,
    );
    if (areAllGamesComplete) {
      const source = 'StartupModals';
      dispatch(advanceToNextWorkshopStep(source));
    }
    dispatch(openLoginModal());
  } else if (targetActivityId.includes('vocabulary')) {
    if (getIsVocabSessionComplete(state, targetActivityId)) {
      dispatch(openVocabSuccessModal());
    } else if (lastAppLaunched === PROGRESS_TYPE.VOCAB) {
      dispatch(openVocabEarlyExitModal());
    } else {
      dispatch(openLoginModal());
    }
  } else if (targetActivityId.includes('close_reading')) {
    if (getIsTFEComplete(state) || getIsTFEPartComplete(state, targetActivityId)) {
      dispatch(openTFESuccessModal());
    } else if (lastAppLaunched === PROGRESS_TYPE.CLOSE_READING) {
      dispatch(openTFEEarlyExitModal());
    } else {
      dispatch(openLoginModal());
    }
  } else if (targetActivityId.includes('ereader')) {
    if (getIsEreaderComplete(state)) {
      dispatch(openEreaderSuccessModal());
    } else if (lastAppLaunched === PROGRESS_TYPE.EREADER) {
      dispatch(openEreaderEarlyExitModal());
    } else {
      dispatch(openLoginModal());
    }
  } else {
    dispatch(openLoginModal());
  }
};
